import i18n from '@/i18n'
import { rgbToHex } from '@/tools/functions'
import { filterRulesMapped } from '@/tools/filterRules'
import { eventService } from '@/services/event.service'
import { mapActions, mapGetters } from 'vuex'
import { DialogType, SnackbarStyle } from '@/constants/constants'
import { dialogTypes } from "@/constants/dialogTypes"
import EventTypeDialogComponent from "@/components/event-type/dialog/EventTypeDialogComponent.vue"
import { DialogEventType, dialogUtil } from "@/tools/dialog"
import { clone } from 'ramda'
import Vue from 'vue'
import { privilegesService } from '@/services/privilegesService'

export default {
    name: 'EventTypeABMComponent',
    components: {
        EventTypeDialogComponent
    },
    data: () => ({
        // headers
        tableHeaders: [],
        // reglas de filtrado
        tableFilterRules: {},
        // datos
        tableData: [],
        // key de los dato de la tabla
        tableDataKey: '_id',
        // botones de cada fila
        rowActions: [],
        // botones del componente Tabla
        tableComponentButtons: [],
        // tamaño de página
        pageSize: undefined,
        // núm de página
        pageNumber: undefined,
        // total de elementos de la base de datos
        pageTotal: 0,
        // dialog de edición
        dialog: {
            ...dialogTypes.EVENT_TYPE,
        },
        // model del dialog
        showEditDialog: false,
        // título del dialog
        editDialogTitle: i18n.t('eventType.editDialog.titleNew'),
        // loading de la tabla
        loadingTable: true,
        // flag de update
        update: false,
        // lenguaje del usuario
        lang: null
    }),
    computed: {
        /**
         * getters del breadcrumb
         */
        ...mapGetters('breadcrumbs', [
            'getBread'
        ])
    },
    created() {
        /*
            Vuex watch del breadcrumb
        */
        this.unwatch = this.$store.watch(
        (state, getters) => getters['breadcrumbs/getBread'],
            (newBreads) => {
                const lastElemet = newBreads[newBreads.length - 1]
                if (!lastElemet) {
                    this.cancel()
                    return
                }
                this.dialog = dialogUtil.getDialog(lastElemet.key, DialogEventType)
            },
        { deep: true })

        if (!privilegesService.validatePrivilegesAnd(['global.events'])) {
            this.rowActions.push(
              {
                id: 1,
                name: i18n.t('modify'),
                color: 'blue',
                icon: 'fa-pen',
                disable (item) {
                  return item.global || item.protected
                }
              },
              {
                id: 2,
                name: i18n.t('delete'),
                color: 'red',
                icon: 'fa-trash-alt',
                disable (item) {
                  return item.global || item.protected
                }
              }
            )
        } else {
            this.rowActions.push(
                {
                  id: 1,
                  name: i18n.t('modify'),
                  color: 'blue',
                  icon: 'fa-pen',
                  disable (item) {
                    return item.protected
                  }
                },
                {
                  id: 2,
                  name: i18n.t('delete'),
                  color: 'red',
                  icon: 'fa-trash-alt',
                  disable (item) {
                    return item.protected
                  }
                }
            )
        }
    },
    beforeDestroy() {
        // elimino la instancia del watcher del breadcrumb
        this.unwatch()
        this.cleanBread()
    },
    mounted() {
        // se obtiene el lenguaje del usuario para mostrar el nombre correcto en mensajes de error
        this.lang = localStorage.getItem('locale')
        // inicialización de los datos del ABM
        this.initialize()
    },
    methods: {
        /**
         * actions del breadcrumb
         */
        ...mapActions('breadcrumbs', [
            'pushBread',
            'cleanBread'
        ]),
        /**
         * actions del snackbar
         */
        ...mapActions('snackbar', [
            'showSnackbar',
            'closeSnackbar'
        ]),
        /**
         * actions del dialog
         */
        ...mapActions('dialog', [
            'openDialog',
            'closeDialog'
        ]),
        /**
         * Para inicializar el componente
         */
        async initialize () {
            // Botón de eliminación múltiple
            this.tableComponentButtons.push(
                {
                    id: 'edit-dialog',
                    tooltip: i18n.t('add'),
                    icon: 'add',
                    action: this.showCreateDialog.bind(this),
                    selectedData: false
                },
                {
                    id: 'masive-delete',
                    tooltip: i18n.t('multipleDelete'),
                    icon: 'delete_outline',
                    action: this.multipleDelete.bind(this),
                    selectedData: true
                })

            // total de tipos de eventos (para el paginador)
            this.pageTotal = await eventService.getEventTypeTotal()

            // Si hay query params en la URL, los setea para incluirlos en la llamada al backend
            if (this.$route.query.pageSize && this.$route.query.pageNumber) {
                this.pageSize = Number(this.$route.query.pageSize)
                this.pageNumber = Number(this.$route.query.pageNumber)

                // Valida que los qery params sean correctos y completos
                if ((this.pageSize && this.pageSize <= 0) || (this.pageNumber && this.pageNumber <= 0) || (this.pageNumber && !this.pageSize) || (!this.pageNumber && this.pageSize)) {
                    this.showSnackbar({
                        visible: true,
                        text: this.$t('error.invalidQueryParams'),
                        timeout: 6000,
                        style: SnackbarStyle.ERROR
                    })
                    this.pageSize = this.pageTotal
                    this.pageNumber = 1
                }
            } else {
                this.pageSize = 50
                this.pageNumber = 1
            }

            // cargo la tabla
            this.getData()
            this.resetFields()
            this.initBread()
        },
        /**
         * Obtiene los datos para la tabla del ABM
         * @param {*} update
         */
        async getData(update = false) {
            this.loadingTable = true
            if (update) {
                this.pageTotal = await eventService.getEventTypeTotal()
                this.pageSize = 50
                this.pageNumber = 1
            }
            const data = await eventService.findAllEventType(this.pageSize, this.pageNumber, !update)
            const lastTableHeaders = clone(this.tableHeaders)
            this.tableData.cleanAndUpdate(data.data)
            if (update) {
                const that = this
                Vue.nextTick(function () {
                    that.tableHeaders.cleanAndUpdate(lastTableHeaders)
                })
            } else {
                this.tableHeaders.cleanAndUpdate(data.headers)
                this.setTableFilterRules()
            }
            this.loadingTable = false
        },
        /**
         * Refrescar la data
         */
        refreshTable() {
            this.getData(true)
        },
        /**
         * Setea las reglas de filtrado
         */
        setTableFilterRules() {
            this.tableFilterRules = {}
            this.tableHeaders.forEach(header => {
                this.tableFilterRules[header.value] = filterRulesMapped[header.filterType]
            })
        },
        /**
         * Inicializa el breadcrumb del dialog
         */
        initBread() {
            const bread = {
                key: DialogEventType,
                disabled: true,
                text: this.$t('eventType.name')
            }
            this.pushBread(bread)
        },
        /**
         * Para eliminar un tipo de evento
         * @param {*} data 
         */
        async delete(data) {
            await eventService.deleteOneEventType(data._id)
            this.getData(true)
        },
        /**
         * Para eliminación múltiple
         * @param {*} data
         */
        async multipleDelete(data) {
            this.openDialog({
                title: this.$t('eventType.multipleDeleteTitle'),
                text: this.$t('eventType.multipleDeleteText'),
                type: DialogType.QUESTION,
                overlayOpacity: 0.5,
                actionButtons: true,
                yesAction: async () => {
                    const ids = data.map(e => e._id)
                    const result = await eventService.deleteManyEventType(ids)
                    this.multipleDeleteResult(result)
                },
                noAction: () => {
                }
            })
        },
        async multipleDeleteResult(result) {
            if (result.error) {
                this.openDialog({
                    title: this.$t('eventType.multipleDeleteErrorTitle'),
                    text: this.$t('eventType.multipleDeleteErrorText', { message: result.message }),
                    type: DialogType.ERROR,
                    overlayOpacity: 0.5,
                    actionButtons: false,
                })
            } else {
                this.getData(true)
            }
        },
        /**
         * Carga los datos para el modal de edición
         * @param {*} id
         */
        async loadFields(id) {
            const data = await eventService.findOneEventType(id)
            data.color = data.color ? (data.color[0] === '#' ? data.color : rgbToHex(data.color)) : data.color
            if (data == null) {
                this.showSnackbar({
                    visible: true,
                    text: this.$t('eventType.couldNotLoadEditDialog'),
                    timeout: 6000,
                    style: SnackbarStyle.ERROR
                })
            } else {
                const modelData = this.dialog.model
                Object.assign(modelData, data)
                this.showUpdateDialog()
            }
        },
        /**
         * Click en un botón de una fila
         * @param {*} button
         * @param {*} data
         */
        rowButtonClicked(button, data) {
            switch (button.id) {
                case 1:
                    this.loadFields(data._id)
                    break
                case 2:
                    this.openDialog({
                        title: this.$t('eventType.deleteOneTitle'),
                        text: this.$t('eventType.deleteOneText', { name: data.name }),
                        type: DialogType.QUESTION,
                        overlayOpacity: 0.5,
                        actionButtons: true,
                        yesAction: () => {
                            this.delete(data)
                        },
                        noAction: () => {
                        }
                    })
                    break
                default:
                    console.log('default')
            }
        },
        /**
         * Click fuera del dialog
         */
        clickOutside() {
            this.cancel()
        },
        /**
         * Cancelar
         */
        cancel() {
            this.resetFields()
            this.showEditDialog = false
        },
        /**
         * Resetea los campos del formulario de creación/actualización
         */
        resetFields() {
            this.dialog.model = {
                _id: null,
                name: {
                    es: null,
                    en: null,
                    pt: null
                },
                color: '#ff0000',
                comment: null,
                enterprises: []
            }
        },
        /**
         * Abre el dialog para creación
         */
        showCreateDialog() {
            this.initBread()
            this.update = false
            this.editDialogTitle= i18n.t('eventType.editDialog.titleNew')
            this.showEditDialog = true
        },
        /**
         * Abre el dialog para actualización
         */
        showUpdateDialog() {
            this.initBread()
            this.update = true
            this.editDialogTitle= i18n.t('eventType.editDialog.titleUpdate')
            this.showEditDialog = true
        },
        /**
         * Paginador externo
         * @param {*} pageSize
         * @param {*} pageNumber
         */
        externalPaginator(pageSize, pageNumber) {
            this.pageNumber = Number(pageNumber)
            this.pageSize = Number(pageSize)
        }
    }
}
